import { Navigate, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import NetworkPanel from '../../components/network/NetworkPanel';
import ConnectionInputForm from '../../components/network/ConnectionInputForm';
import { useNetworkData } from '../../hooks/network/useNetworkData';
import SetupPageLayout from '../../components/setup/SetupPageLayout';
import api from '../../API';
import { AGENT_SETUP_BASE, getAdminStep } from '../../constants/setupRoadmapSteps';

const networkStep = getAdminStep('network');

function NetworkConnectivity() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { local, isLoading, connections, connectedList, pendingList } = useNetworkData();

    // Derived values for effects (no hooks - safe to compute here)
    const connectionApiUrl = pendingList?.[0]?.url || connectedList?.[0]?.url || '';
    const getBaseUrl = (url) => {
        if (!url) return '';
        return url.replace(/\/dsa\/api\/v1.*$/, '').replace(/\/bdsa\/api\/v1.*$/, '').replace(/\/api\/v1.*$/, '').replace(/\/$/, '');
    };
    const connectionBaseUrl = getBaseUrl(connectionApiUrl);

    // Refetch connection data when navigating to this page
    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: ['local-connections'] });
    }, []);

    const [nodeUrl, setNodeUrl] = useState(() => {
        try {
            const stored = localStorage.getItem('bdsa_node_url');
            return stored || '';
        } catch (error) {
            console.error('Error reading nodeUrl from localStorage:', error);
            return '';
        }
    });
    const [connectionMsg, setConnectionMsg] = useState('');

    useEffect(() => {
        try {
            if (nodeUrl) {
                localStorage.setItem('bdsa_node_url', nodeUrl);
            } else {
                localStorage.removeItem('bdsa_node_url');
            }
        } catch (error) {
            console.error('Error saving nodeUrl to localStorage:', error);
        }
    }, [nodeUrl]);

    useEffect(() => {
        if (connectionBaseUrl) {
            setNodeUrl(connectionBaseUrl);
        }
    }, [connectionBaseUrl]);

    const initiateConnectionMutation = useMutation({
        mutationFn: (url) => api.post('network/connection', {query: {url}}),
        onSuccess: (d) => {
            if (d.status === 200) {
                const body = d.data;
                if (body && typeof body === 'object') {
                    if (body.success === true) {
                        const remoteUrl = body.remoteUrl || nodeUrl;
                        const msg = body.message || `Connection request sent to ${remoteUrl}. Awaiting approval.`;
                        setConnectionMsg(msg);
                        queryClient.invalidateQueries({ queryKey: ['local-connections'] });
                    } else {
                        const err = body.error || 'Remote server indicated failure.';
                        setConnectionMsg(`Failed to send connection request: ${err}`);
                    }
                } else {
                    setConnectionMsg('Connection request response was not JSON. Please try again or contact support.');
                }
            } else {
                const err = (typeof d.data === 'string' ? d.data : (d.data?.error || 'Request failed'));
                setConnectionMsg(`Failed to send connection request: ${err}`);
            }
        },
        onError: (e) => {
            setConnectionMsg(`Failed to send connection request: ${e?.message || 'Unknown error'}`);
        },
    });

    // Conditional returns after all hooks (stable hook count on every render)
    if (!isLoading && !local) {
        return <Navigate to="/admin/setup/initial" replace />;
    }
    if (isLoading) {
        return <div>Loading...</div>;
    }

    const savedIsHub = local?.isHub || false;
    const hasPendingConnection = pendingList?.length > 0;
    const hasActiveConnection = connectedList?.length > 0;
    const shouldDisableConnectionInput = hasPendingConnection || hasActiveConnection;
    const initiateConnection = () => {
        if (!nodeUrl.trim() || shouldDisableConnectionInput) return;
        initiateConnectionMutation.mutate(nodeUrl);
    };
    const disabledMessage = hasActiveConnection
        ? 'You are already connected to a hub. Disconnect to connect to a different hub.'
        : 'A connection request is pending. Cancel or delete the existing request to make a new one.';

    return (
        <SetupPageLayout
            stepNumber={networkStep.number}
            totalSteps={networkStep.total}
            title="Network Connectivity"
            description="Manage connections to other BDSA nodes. Spokes connect to a hub; hubs approve spoke requests. After you join a hub, other spokes appear under that hub as Direct or Proxied (how this node loads their data). You can switch that preference without recreating the peer folder. Enable Auto-sync on the hub link to pull updates automatically. You can finish this later if needed."
            onNext={() => navigate(AGENT_SETUP_BASE)}
            onPrevious={() => navigate('/admin/setup/initial')}
            nextDisabled={false}
            nextLabel="Next: Agent Management"
            previousLabel="Initial Server Configuration"
        >
            <h1>Network Connectivity</h1>
            
            {!savedIsHub && (
                <ConnectionInputForm
                    nodeUrl={nodeUrl}
                    onUrlChange={(e) => {
                        if (!shouldDisableConnectionInput) {
                            setNodeUrl(e.target.value);
                        }
                    }}
                    onSubmit={initiateConnection}
                    disabled={shouldDisableConnectionInput}
                    disabledMessage={shouldDisableConnectionInput ? disabledMessage : null}
                    connectionMsg={connectionMsg}
                />
            )}
            
            <NetworkPanel 
                mode={import.meta.env.REACT_APP_NETWORK_MODE || 'hub-spoke'} 
                role={savedIsHub ? 'hub' : 'spoke'} 
            />
        </SetupPageLayout>
    );
}

export default NetworkConnectivity;
